import { motion } from "framer-motion";
import { Reveal, SectionTag, SectionTitle, StatusBadge, scrollToId } from "./shared";
import { PROJECTS, YEIDA_SECTORS } from "../data/site";
import { ArrowUpRight } from "lucide-react";

const PLOT_SECTOR_IDS = ["s28", "s29", "s30", "s31", "s32"];

function openSectorTab(id) {
  window.dispatchEvent(new CustomEvent("corridor:open-tab", { detail: id }));
}

function SectorPlotCard({ s, i }) {
  return (
    <motion.button
      data-testid={`yeida-plot-card-${s.id}`}
      onClick={() => openSectorTab(s.id)}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: (i % 5) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group card-lux rounded-[2px] overflow-hidden text-left flex flex-col hover:border-[#D4AF37]/45 transition-colors"
    >
      <div className="relative h-[150px] overflow-hidden">
        <img src={s.image} alt={`${s.sector} plots`} className="w-full h-full object-cover img-cinematic transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-black/30 to-black/40" />
        <div className="absolute top-3 left-3"><StatusBadge status={s.status} /></div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">YEIDA &bull; {s.tag}</p>
        <h3 className="font-serif-lux text-xl text-[#F5F5F0] mb-3">{s.sector} Plots</h3>
        <p className="text-[12px] text-[#A1A1AA] leading-relaxed">{s.plotSummary}</p>
        <p className="text-[12px] text-[#F3E5AB] leading-relaxed mt-1 mb-4">{s.rateSummary}</p>
        <span className="mt-auto flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F3E5AB] opacity-70 group-hover:opacity-100 transition-opacity">
          Full plot details <ArrowUpRight size={13} />
        </span>
      </div>
    </motion.button>
  );
}

export function Projects() {
  return (
    <section id="projects" className="relative py-28 lg:py-40 bg-[#0D0D0D]" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTag>GYC Project Collection</SectionTag>
        <div className="grid lg:grid-cols-2 gap-8 items-end mb-14">
          <SectionTitle>The <span className="text-gold-gradient italic">Collection.</span></SectionTitle>
          <Reveal delay={0.15}>
            <p className="text-sm text-[#71717A] leading-relaxed max-w-md lg:ml-auto">
              Featured inventory across Gaur Yamuna City, shown where currently available and verified.
              Sample presentation - confirm live availability with our desk.
            </p>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
          {PROJECTS.map((p, i) => (
            <motion.button
              key={p.name}
              data-testid={`project-card-${i}`}
              onClick={() => scrollToId("inventory")}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative h-[340px] overflow-hidden rounded-[2px] border border-[#D4AF37]/14 text-left"
            >
              <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover img-cinematic transition-transform duration-[1.6s] ease-out group-hover:scale-108" style={{ transform: "scale(1.02)" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-black/10 group-hover:via-black/20 transition-colors duration-700" />
              <div className="absolute top-4 left-4"><StatusBadge status={p.status} /></div>
              <div className="absolute bottom-0 inset-x-0 p-6">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1.5">{p.type} &bull; {p.location}</p>
                <h3 className="font-serif-lux text-2xl text-[#F5F5F0] mb-1">{p.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-[#A1A1AA]">{p.config} &bull; {p.price}</span>
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F3E5AB] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    Explore <ArrowUpRight size={13} />
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-20" data-testid="yeida-plots-collection">
          <div className="grid lg:grid-cols-2 gap-6 items-end mb-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">YEIDA Sector Plots &bull; Sectors 28 - 32</p>
              <h3 className="font-serif-lux text-3xl sm:text-4xl text-[#F5F5F0]">Authority Plots <span className="text-gold-gradient italic">Around the Airport.</span></h3>
            </div>
            <p className="text-sm text-[#71717A] leading-relaxed max-w-md lg:ml-auto">
              Direct-from-YEIDA industrial and planned sectors nearest to Noida International Airport.
              Tap a sector for scheme codes, plot sizes, allotment rates and eligibility.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="yeida-plots-grid">
            {YEIDA_SECTORS.filter((s) => PLOT_SECTOR_IDS.includes(s.id)).map((s, i) => <SectorPlotCard key={s.id} s={s} i={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
